import { Field } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PlayerPickerButton } from '@/components/composite/PlayerPickerButton';
import { JerseyPickerButton } from '@/components/composite/JerseyPickerButton';
import { TeamPickerButton } from '@/components/composite/TeamPickerButton';
import { ImageUploadButton } from '@low6-pickem/ui';
import { uploadImage } from '@/api/upload';
import { toast } from 'sonner';
import type { SelectedFixtureData } from './types';

interface OptionImageFieldsProps {
  index: number;
  optionImageEn: string;
  optionImageFr: string;
  selectedTeams?: string[];
  selectedFixtureData?: SelectedFixtureData | null;
  questionType: string;
  setFieldValue: (field: string, value: any) => void;
  isEditable: boolean;
  /** When set, auto-selects this league and disables the league selector in picker modals */
  restrictedLeagueId?: string;
  /** Pickem ID for image upload organization */
  pickemId?: string;
  /** Contest League IP flag — drives licensed vs generic image auto-fill */
  leagueIp?: boolean;
}

export function OptionImageFields({
  index,
  optionImageEn,
  optionImageFr: _optionImageFr,
  selectedTeams = [],
  selectedFixtureData = null,
  questionType,
  setFieldValue,
  isEditable,
  restrictedLeagueId,
  pickemId,
  leagueIp = false,
}: OptionImageFieldsProps) {
  const isPlayerSelection = questionType === 'player' || questionType === 'player_over_under';
  const isTeamSelection = questionType === 'team' || questionType === 'team_over_under';
  const isCustom = questionType === 'custom';

  // Build fixtureData for grading_data (same shape as FixtureOptionsPopulator / OverUnderOptionsGenerator)
  const fixtureDataForGrading = selectedFixtureData?.fixture
    ? {
        fixtureId: selectedFixtureData.fixtureId,
        homeTeamId: selectedFixtureData.fixture.homeTeamId || '',
        awayTeamId: selectedFixtureData.fixture.awayTeamId || '',
        homeTeamName: selectedFixtureData.fixture.homeTeamName || '',
        awayTeamName: selectedFixtureData.fixture.awayTeamName || '',
        homeTeamAbbreviation: selectedFixtureData.fixture.homeTeamAbbreviation,
        awayTeamAbbreviation: selectedFixtureData.fixture.awayTeamAbbreviation,
        scheduledDate: selectedFixtureData.fixture.scheduledDate || new Date().toISOString(),
        status: (selectedFixtureData.fixture.status || 'scheduled') as
          | 'scheduled'
          | 'live'
          | 'finished'
          | 'postponed'
          | 'cancelled',
      }
    : undefined;

  // Upload function for ImageUploadButton
  const handleUpload = async (data: { path: string; filename: string; imageData: string }) => {
    const result = await uploadImage(data);
    toast.success('Image uploaded successfully');
    return { imageUrl: result.imageUrl };
  };

  return (
    <div className="flex flex-col gap-2">
      <Label>Option Image</Label>
      <div className="relative w-full h-32 mb-2 border rounded-md overflow-hidden bg-gray-50">
        <img
          src={optionImageEn || '/no-jersey.png'}
          alt="Option preview"
          className="w-full h-full object-contain"
          onError={e => {
            e.currentTarget.src = '/no-jersey.png';
          }}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Field
          as={Input}
          name={`options.${index}.option_image_en`}
          data-testid={`question-option-${index}-option_image_en`}
          placeholder="https://example.com/image.jpg or use pickers below"
          disabled={!isEditable}
          className="min-w-[200px] flex-1"
        />
        {(isEditable && isPlayerSelection) || (isEditable && isCustom) ? (
          <PlayerPickerButton
            selectedTeams={selectedTeams}
            onSelect={selection => {
              setFieldValue(`options.${index}.option_text_en`, selection.playerName);
              setFieldValue(`options.${index}.option_text_fr`, selection.playerName);
              setFieldValue(`options.${index}.option_image_en`, selection.jerseyUrl);
              setFieldValue(`options.${index}.option_image_fr`, selection.jerseyUrl);
              // Auto-populate team logo in third_image fields
              setFieldValue(`options.${index}.third_image_en`, selection.teamLogoUrl);
              setFieldValue(`options.${index}.third_image_fr`, selection.teamLogoUrl);
              // Populate grading_data for auto-grading (same technique as FixtureOptionsPopulator for teams)
              if (isPlayerSelection && (selection.playerId || selection.teamId)) {
                setFieldValue(`options.${index}.grading_data`, {
                  teamId: selection.teamId,
                  playerId: selection.playerId,
                  teamName: selection.teamName,
                  playerName: selection.playerName,
                  playerPosition: selection.playerPosition || '',
                  fixtureData: fixtureDataForGrading,
                });
              }
            }}
            fieldLabel={`Option ${index + 1}`}
            restrictedLeagueId={restrictedLeagueId}
            leagueIp={leagueIp}
          />
        ) : null}

        {(isEditable && isTeamSelection) || (isEditable && isCustom) ? (
          <TeamPickerButton
            selectedTeams={selectedTeams}
            onSelect={selection => {
              setFieldValue(`options.${index}.option_text_en`, selection.teamName);
              setFieldValue(`options.${index}.option_text_fr`, selection.teamName);
              setFieldValue(`options.${index}.option_image_en`, selection.teamLogoUrl);
              setFieldValue(`options.${index}.option_image_fr`, selection.teamLogoUrl);
              // Populate grading_data for auto-grading (same technique as FixtureOptionsPopulator)
              if (selection.teamId) {
                setFieldValue(`options.${index}.grading_data`, {
                  teamId: selection.teamId,
                  teamName: selection.teamName,
                  teamMarket: selection.teamMarket,
                  teamAbbr: selection.teamAbbr,
                  fixtureData: fixtureDataForGrading,
                });
              }
            }}
            fieldLabel={`Option ${index + 1}`}
            restrictedLeagueId={restrictedLeagueId}
            leagueIp={leagueIp}
          />
        ) : null}

        {isEditable && (
          <JerseyPickerButton
            onSelect={url => {
              // Set both English and Spanish to the same image URL
              setFieldValue(`options.${index}.option_image_en`, url);
              setFieldValue(`options.${index}.option_image_fr`, url);
            }}
            fieldLabel={`Option ${index + 1} Image`}
            restrictedLeagueId={restrictedLeagueId}
          />
        )}

        {isEditable && pickemId && (
          <ImageUploadButton
            onUpload={url => {
              // Set both English and Spanish to the same image URL
              setFieldValue(`options.${index}.option_image_en`, url);
              setFieldValue(`options.${index}.option_image_fr`, url);
            }}
            path={`pickems/assets/${pickemId}`}
            filename="option-image"
            uploadFn={handleUpload}
            onError={message => toast.error(message)}
            title="Upload custom image"
          />
        )}
      </div>
    </div>
  );
}
